import {
  BoxesPerPeriod,
  BraintreePlanId,
  DiscountCodeIdentifier,
  ItemLimit,
  PauseCodeIdentifier,
  TiersAccessible,
} from "@glamcorner/gc-types/lib/subscription";

/**
 * The capabilities and restrictions that a Plan applies to a user.
 */
export interface PlanInformation {
  available: boolean;
  boxesPerPeriod: BoxesPerPeriod;
  itemLimit: ItemLimit;
  tiersAccessible: TiersAccessible;
  name: string; // Human friendly plan name
  price: number;
  isLegacy: boolean; // TODO: Remove once we fully migrate
  discountCodeIdentifiers: DiscountCodeIdentifier[];
  pauseCodeIdentifier: PauseCodeIdentifier;
}
export type PlanInformationWithBraintreeId = PlanInformation & {
  braintreePlanId: BraintreePlanId;
};

export type PlanInformationMap = Record<BraintreePlanId, PlanInformation>;

/**
 * The source of truth for the capabilities and restrictions that a Braintree
 * plan gives a user.
 */
const PLAN_INFORMATION_MAP: PlanInformationMap = {
  "legacy-99-unlimited-plan-3-items": {
    available: false,
    boxesPerPeriod: "unlimited",
    itemLimit: 3,
    tiersAccessible: ["legacy"],
    name: "Starter",
    price: 99,
    isLegacy: true,
    discountCodeIdentifiers: ["A"],
    pauseCodeIdentifier: "vk7g",
  },
  "2xcg": {
    available: false,
    boxesPerPeriod: "unlimited",
    itemLimit: 3,
    tiersAccessible: ["legacy"],
    name: "Starter",
    price: 99,
    isLegacy: true,
    discountCodeIdentifiers: ["A"],
    pauseCodeIdentifier: "vk7g",
  },
  gx2b: {
    available: true,
    boxesPerPeriod: 1,
    itemLimit: 3,
    tiersAccessible: ["legacy"],
    name: "Starter",
    price: 99,
    isLegacy: true,
    discountCodeIdentifiers: ["A"],
    pauseCodeIdentifier: "vk7g",
  },
  p9wb: {
    available: true,
    boxesPerPeriod: "unlimited",
    itemLimit: 3,
    tiersAccessible: ["legacy"],
    name: "Unlimited",
    price: 149,
    isLegacy: true,
    discountCodeIdentifiers: ["B"],
    pauseCodeIdentifier: "fmv6",
  },
  "lite-plan-2-items": {
    available: true,
    boxesPerPeriod: 1,
    itemLimit: 2,
    tiersAccessible: ["lite"],
    name: "Lite",
    price: 79,
    isLegacy: false,
    discountCodeIdentifiers: ["X"],
    pauseCodeIdentifier: "lite-plan-2-items-pause",
  },
  "lite-plan-3-items": {
    available: true,
    boxesPerPeriod: 1,
    itemLimit: 3,
    tiersAccessible: ["lite"],
    name: "Lite",
    price: 99,
    isLegacy: false,
    discountCodeIdentifiers: ["C", "A"], // 'A' allows gift cards created for the legacy cc99 plan to be redeemed for this plan
    pauseCodeIdentifier: "lite-plan-3-items-pause",
  },
  "lite-plan-4-items": {
    available: true,
    boxesPerPeriod: 1,
    itemLimit: 4,
    tiersAccessible: ["lite"],
    name: "Lite",
    price: 119,
    isLegacy: false,
    discountCodeIdentifiers: ["X"],
    pauseCodeIdentifier: "lite-plan-4-items-pause",
  },
  "lite-plan-5-items": {
    available: true,
    boxesPerPeriod: 1,
    itemLimit: 5,
    tiersAccessible: ["lite"],
    name: "Lite",
    price: 149,
    isLegacy: false,
    discountCodeIdentifiers: ["X"],
    pauseCodeIdentifier: "lite-plan-5-items-pause",
  },
  "lite-plan-6-items": {
    available: true,
    boxesPerPeriod: 1,
    itemLimit: 6,
    tiersAccessible: ["lite"],
    name: "Lite",
    price: 169,
    isLegacy: false,
    discountCodeIdentifiers: ["X"],
    pauseCodeIdentifier: "lite-plan-6-items-pause",
  },
  "premium-plan-2-items": {
    available: true,
    boxesPerPeriod: 1,
    itemLimit: 2,
    tiersAccessible: ["lite", "premium"],
    name: "Premium",
    price: 119,
    isLegacy: false,
    discountCodeIdentifiers: ["X"],
    pauseCodeIdentifier: "premium-plan-2-items-pause",
  },
  "premium-plan-3-items": {
    available: true,
    boxesPerPeriod: 1,
    itemLimit: 3,
    tiersAccessible: ["lite", "premium"],
    name: "Premium",
    price: 169,
    isLegacy: false,
    discountCodeIdentifiers: ["D"],
    pauseCodeIdentifier: "premium-plan-3-items-pause",
  },
  "premium-plan-4-items": {
    available: true,
    boxesPerPeriod: 1,
    itemLimit: 4,
    tiersAccessible: ["lite", "premium"],
    name: "Premium",
    price: 219,
    isLegacy: false,
    discountCodeIdentifiers: ["X"],
    pauseCodeIdentifier: "premium-plan-4-items-pause",
  },
  "premium-plan-5-items": {
    available: true,
    boxesPerPeriod: 1,
    itemLimit: 5,
    tiersAccessible: ["lite", "premium"],
    name: "Premium",
    price: 269,
    isLegacy: false,
    discountCodeIdentifiers: ["X"],
    pauseCodeIdentifier: "premium-plan-5-items-pause",
  },
  "premium-plan-6-items": {
    available: true,
    boxesPerPeriod: 1,
    itemLimit: 6,
    tiersAccessible: ["lite", "premium"],
    name: "Premium",
    price: 319,
    isLegacy: false,
    discountCodeIdentifiers: ["X"],
    pauseCodeIdentifier: "premium-plan-6-items-pause",
  },
  "ultimate-plan-2-items": {
    available: true,
    boxesPerPeriod: 1,
    itemLimit: 2,
    tiersAccessible: ["lite", "premium", "ultimate"],
    name: "Ultimate",
    price: 169,
    isLegacy: false,
    discountCodeIdentifiers: ["X"],
    pauseCodeIdentifier: "ultimate-plan-2-items-pause",
  },
  "ultimate-plan-3-items": {
    available: true,
    boxesPerPeriod: 1,
    itemLimit: 3,
    tiersAccessible: ["lite", "premium", "ultimate"],
    name: "Ultimate",
    price: 239,
    isLegacy: false,
    discountCodeIdentifiers: ["X"],
    pauseCodeIdentifier: "ultimate-plan-3-items-pause",
  },
  "ultimate-plan-4-items": {
    available: true,
    boxesPerPeriod: 1,
    itemLimit: 4,
    tiersAccessible: ["lite", "premium", "ultimate"],
    name: "Ultimate",
    price: 319,
    isLegacy: false,
    discountCodeIdentifiers: ["X"],
    pauseCodeIdentifier: "ultimate-plan-4-items-pause",
  },
  "ultimate-plan-5-items": {
    available: true,
    boxesPerPeriod: 1,
    itemLimit: 5,
    tiersAccessible: ["lite", "premium", "ultimate"],
    name: "Ultimate",
    price: 389,
    isLegacy: false,
    discountCodeIdentifiers: ["X"],
    pauseCodeIdentifier: "ultimate-plan-5-items-pause",
  },
  "ultimate-plan-6-items": {
    available: true,
    boxesPerPeriod: 1,
    itemLimit: 6,
    tiersAccessible: ["lite", "premium", "ultimate"],
    name: "Ultimate",
    price: 459,
    isLegacy: false,
    discountCodeIdentifiers: ["X"],
    pauseCodeIdentifier: "ultimate-plan-6-items-pause",
  },
};

/**
 * Get the capabilities and restrictions to apply to a user from a Braintree
 * plan ID.
 */
export const planInformationFromBraintreePlanId = (brainTreeId: BraintreePlanId): PlanInformation | undefined => {
  return PLAN_INFORMATION_MAP[brainTreeId];
};

/**
 * Get how many boxes a user can order from a Braintree plan ID.
 */
export const boxesPerPeriodFromBraintreePlanId = (brainTreeId: BraintreePlanId): BoxesPerPeriod | undefined => {
  return planInformationFromBraintreePlanId(brainTreeId)?.boxesPerPeriod;
};

/**
 * Get how many items a user can have from a Braintree plan ID.
 */
export const itemLimitFromBraintreePlanId = (brainTreeId: BraintreePlanId): ItemLimit | undefined => {
  return planInformationFromBraintreePlanId(brainTreeId)?.itemLimit;
};

/**
 * Get which tiers a user has access to from a Braintree plan ID.
 */
export const tiersAccessibleFromBraintreePlanId = (brainTreeId: BraintreePlanId): TiersAccessible | undefined => {
  return planInformationFromBraintreePlanId(brainTreeId)?.tiersAccessible;
};

/**
 * Get pause discount code from a Braintree plan ID.
 */
export const pauseCodeFromBraintreePlanId = (brainTreeId: BraintreePlanId): PauseCodeIdentifier | undefined => {
  return planInformationFromBraintreePlanId(brainTreeId)?.pauseCodeIdentifier;
};

// Gets an array of unique boxesPerPeriod, excluding all the legacy ones
export const itemLimitAggregate = (): ItemLimit[] => {
  const itemLimits: ItemLimit[] = [];

  Object.keys(PLAN_INFORMATION_MAP).forEach((brainTreeId: BraintreePlanId) => {
    const plan: PlanInformation = PLAN_INFORMATION_MAP[brainTreeId];
    if (plan.isLegacy) {
      return;
    }

    itemLimits.push(plan.itemLimit);
  });

  return Array.from(new Set(itemLimits));
};

export const filterPlansByItemLimit = (itemLimit: ItemLimit): PlanInformationMap => {
  const planMap: PlanInformationMap = {};

  Object.keys(PLAN_INFORMATION_MAP).forEach((brainTreeId: BraintreePlanId) => {
    const plan: PlanInformation = PLAN_INFORMATION_MAP[brainTreeId];

    if (plan.isLegacy || plan.itemLimit != itemLimit) {
      return;
    }

    planMap[brainTreeId] = plan;
  });

  return planMap;
};

/**
 * Get a sorted list of the plans which are available for users to select.
 *
 * @param includeLegacy Whether to include legacy plans as being available.  If
 * they are included, they are sorted last.
 */
export const getAvailablePlansSorted = (includeLegacy = false): PlanInformationWithBraintreeId[] => {
  return Object.entries<PlanInformation>(PLAN_INFORMATION_MAP)
    .map(([braintreePlanId, plan]) => ({
      ...plan,
      braintreePlanId,
    }))
    .filter((plan) => plan.available)
    .filter((plan) => includeLegacy || !plan.isLegacy)
    .sort((a, b) => {
      if (a.isLegacy === b.isLegacy) {
        return 0; // keep original order
      } else if (a.isLegacy) {
        return 1; // sort a after b
      } else if (b.isLegacy) {
        return -1; // sort b after a
      }

      // Only here to appease TS, which does not realise that this line would
      // never be reached.
      throw new Error("assert 0");
    });
};

export const isLegacyPlan = (planId: string): boolean => {
  return PLAN_INFORMATION_MAP[planId].isLegacy;
};

export const allPlansCSV = (): string => {
  const csv = [];
  const headings = [
    "id",
    "available",
    "boxesPerPeriod",
    "itemLimit",
    "tiersAccessible",
    "name",
    "price",
    "isLegacy",
    "discountCodeIdentifier",
    "pauseCodeIdentifier",
  ];

  csv.push(headings);

  Object.keys(PLAN_INFORMATION_MAP).forEach((braintreeId: BraintreePlanId) => {
    const plan: PlanInformation = PLAN_INFORMATION_MAP[braintreeId];

    csv.push([
      braintreeId,
      plan.available ? 1 : 0,
      plan.boxesPerPeriod,
      plan.itemLimit.toString(),
      plan.tiersAccessible.join("|"),
      plan.name,
      plan.price,
      plan.isLegacy ? 1 : 0,
      plan.discountCodeIdentifiers[0],
      plan.pauseCodeIdentifier,
    ]);
  });

  return csv.map((row) => row.join(",")).join("\n");
};
